import { Folder, Plus, MoreVertical, Trash2, AlertCircle } from 'lucide-react';
import React, { useState } from 'react';
import { api } from '../services/api';

interface FolderListProps {
  folders: string[];
  currentPath: string;
  onNavigate: (path: string) => void;
  onRefresh?: () => void;
}

interface CreateFolderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (name: string) => void;
  isCreating: boolean;
}

function CreateFolderModal({ isOpen, onClose, onCreate, isCreating }: CreateFolderModalProps) {
  const [folderName, setFolderName] = useState('');
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!folderName.trim()) { setError('Folder name is required'); return; }
    if (folderName.length > 100) { setError('Max 100 characters'); return; }
    if (/[<>:"/\\|?*\x00-\x1f]/.test(folderName)) { setError('Invalid characters in name'); return; }
    onCreate(folderName.trim());
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl">
        <div className="px-6 pt-6 pb-2">
          <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mb-5 sm:hidden" />
          <h3 className="text-base font-semibold text-gray-900">New Folder</h3>
        </div>
        <form onSubmit={handleSubmit} className="px-6 pb-6 space-y-4">
          <div>
            <input
              type="text"
              value={folderName}
              onChange={e => setFolderName(e.target.value)}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
              placeholder="Folder name"
              autoFocus
              disabled={isCreating}
            />
            {error && <p className="mt-1.5 text-xs text-red-500">{error}</p>}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={isCreating}
              className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-200 transition-colors touch-manipulation"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isCreating || !folderName.trim()}
              className="flex-1 py-3 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 transition-colors touch-manipulation disabled:opacity-40"
            >
              {isCreating ? 'Creating…' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface DeleteConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isDeleting: boolean;
  folderName: string;
}

function DeleteConfirmModal({ isOpen, onClose, onConfirm, isDeleting, folderName }: DeleteConfirmModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl">
        <div className="px-6 pt-6 pb-2">
          <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mb-5 sm:hidden" />
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
              <AlertCircle className="h-5 w-5 text-red-600" />
            </div>
            <h3 className="text-base font-semibold text-gray-900">Delete "{folderName}"?</h3>
          </div>
          <p className="text-sm text-gray-500 pl-13">
            All files and subfolders inside will be permanently deleted. This can't be undone.
          </p>
        </div>
        <div className="flex gap-2 px-6 py-4">
          <button
            onClick={onClose}
            disabled={isDeleting}
            className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-200 transition-colors touch-manipulation"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isDeleting}
            className="flex-1 py-3 bg-red-600 text-white rounded-xl text-sm font-medium hover:bg-red-700 transition-colors touch-manipulation disabled:opacity-50"
          >
            {isDeleting ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function FolderList({ folders, currentPath, onNavigate, onRefresh }: FolderListProps) {
  const [selectedFolder, setSelectedFolder] = useState<{ name: string; path: string } | null>(null);
  const [actionMenuOpen, setActionMenuOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleCreate = async (name: string) => {
    setIsCreating(true);
    try {
      await api.createDirectory(currentPath, name);
      setCreateModalOpen(false);
      onRefresh?.();
    } catch (err) {
      console.error('Failed to create folder:', err);
    } finally {
      setIsCreating(false);
    }
  };

  const handleDelete = async (path: string) => {
    setIsDeleting(true);
    try {
      await api.deleteDirectory(path, true);
      setDeleteModalOpen(false);
      setSelectedFolder(null);
      onRefresh?.();
    } catch (err) {
      console.error('Failed to delete folder:', err);
    } finally {
      setIsDeleting(false);
    }
  };

  let longPressTimer: ReturnType<typeof setTimeout>;

  const handlePressStart = (name: string, path: string) => {
    longPressTimer = setTimeout(() => {
      setSelectedFolder({ name, path });
      setActionMenuOpen(true);
    }, 500);
  };

  const handlePressEnd = () => clearTimeout(longPressTimer);

  return (
    <>
      <div>
        <p className="section-label mb-3">Folders</p>
        <div className="folder-grid">

          {/* Existing folder cards */}
          {folders.map(folder => {
            const folderPath = currentPath ? `${currentPath}/${folder}` : folder;
            return (
              <div key={folder} className="relative group">
                <button
                  onClick={() => onNavigate(folderPath)}
                  onMouseDown={() => handlePressStart(folder, folderPath)}
                  onMouseUp={handlePressEnd}
                  onMouseLeave={handlePressEnd}
                  onTouchStart={() => handlePressStart(folder, folderPath)}
                  onTouchEnd={handlePressEnd}
                  className="w-full aspect-square bg-slate-800 hover:bg-slate-700 active:bg-slate-600 rounded-2xl flex flex-col items-center justify-center gap-2 p-3 transition-colors touch-manipulation"
                >
                  <Folder className="h-9 w-9 sm:h-10 sm:w-10 text-blue-300 flex-shrink-0" />
                  <span className="text-white text-xs sm:text-sm font-medium text-center leading-tight line-clamp-2 w-full px-1">
                    {folder}
                  </span>
                </button>

                {/* Desktop: three-dot */}
                <button
                  onClick={() => {
                    setSelectedFolder({ name: folder, path: folderPath });
                    setActionMenuOpen(true);
                  }}
                  className="absolute top-2 right-2 p-1.5 bg-white/10 hover:bg-white/25 rounded-lg opacity-0 group-hover:opacity-100 hidden sm:flex items-center justify-center transition-all"
                >
                  <MoreVertical className="h-4 w-4 text-white" />
                </button>
              </div>
            );
          })}

          {/* New folder card — always at the end */}
          <button
            onClick={() => setCreateModalOpen(true)}
            className="aspect-square bg-gray-50 hover:bg-gray-100 border-2 border-dashed border-gray-200 hover:border-gray-300 rounded-2xl flex flex-col items-center justify-center gap-2 transition-colors touch-manipulation"
          >
            <Plus className="h-7 w-7 sm:h-8 sm:w-8 text-gray-300" />
            <span className="text-xs sm:text-sm text-gray-400 font-medium">New Folder</span>
          </button>

        </div>
      </div>

      {/* Action menu */}
      {actionMenuOpen && selectedFolder && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60" onClick={() => setActionMenuOpen(false)}>
          <div className="bg-white w-full sm:max-w-xs rounded-t-2xl sm:rounded-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mt-3 sm:hidden" />
            <p className="px-5 py-3 text-xs text-gray-400 font-medium">{selectedFolder.name}</p>
            <div className="border-t border-gray-100">
              <button
                onClick={() => { setActionMenuOpen(false); setDeleteModalOpen(true); }}
                className="w-full flex items-center gap-3 px-5 py-4 text-red-600 hover:bg-red-50 transition-colors touch-manipulation"
              >
                <Trash2 className="h-5 w-5" />
                <span className="font-medium">Delete Folder</span>
              </button>
              <button
                onClick={() => setActionMenuOpen(false)}
                className="w-full flex items-center justify-center px-5 py-4 text-gray-500 hover:bg-gray-50 transition-colors border-t border-gray-100 touch-manipulation"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <CreateFolderModal
        key={createModalOpen ? 'open' : 'closed'}
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onCreate={handleCreate}
        isCreating={isCreating}
      />

      {selectedFolder && (
        <DeleteConfirmModal
          isOpen={deleteModalOpen}
          onClose={() => { setDeleteModalOpen(false); setSelectedFolder(null); }}
          onConfirm={() => handleDelete(selectedFolder.path)}
          isDeleting={isDeleting}
          folderName={selectedFolder.name}
        />
      )}
    </>
  );
}
